<template>
  <div data-grm-comp="RoomTestbankHistoryListTable">
    <div class="h-full flex flex-col hidden lg:flex">
      <table>
        <colgroup>
          <col width="3%">
          <col width="4%">
          <col width="12%">
          <col width="5%">
          <col width="5%">
          <col width="18%">
          <col width="8%">
        </colgroup>
        <thead class="h-8 bg-gray-200">
        <tr>
          <th>{{ $t("common.number") }}</th>
          <th>{{ $t("testBank.kind") }}</th>
          <th>{{ $t("setting.name.title") }}</th>
          <th>{{ $t("testBank.number.question") }}</th>
          <th>{{ $t("testBank.number.taker") }}</th>
          <th>{{ $t("testBank.period") }}</th>
          <th>{{ $t("testBank.limit") }}</th>
        </tr>
        </thead>
      </table>
      <div class="max-h-full overflow-y-auto scrollbar-hide">
        <table>
          <colgroup>
            <col width="3%">
            <col width="4%">
            <col width="12%">
            <col width="5%">
            <col width="5%">
            <col width="18%">
            <col width="8%">
          </colgroup>
          <tbody>
          <template>
            <tr v-for="(exam, i) in testbankExamHistoryList?.content"
                :key="exam.roomExamId"
                class="cursor-pointer transition hover:bg-gray-100"
                @click="onSelectTestbankExamHistory(exam)"
            >
              <td>{{ examNum(i) }}</td>
              <td>{{ examType(exam) }}</td>
              <td>{{ exam.examTitle }}</td>
              <td>{{ exam.bankExam.questionList?.length }}</td>
              <td>{{ exam.roomExamUserCnt }}</td>
              <td>{{ exam.beginDate }} ~ {{ exam.endDate }}</td>
              <td>{{ examDuration(exam) }}</td>
            </tr>
            <tr v-if="!testbankExamHistoryList?.content?.length">
              <td colspan="7" class="text-center">{{ $t("testBank.noHistory") }}</td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
    </div>
    <div class="flex flex-col gap-2 lg:hidden">
      <div v-for="(exam, i) in testbankExamHistoryList?.content"
           :key="exam.roomExamId"
           class="p-2 rounded bg-gray-100"
      >
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("common.number") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ examNum(i) }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("testBank.kind") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ examType(exam) }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("setting.name.title") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ exam.examTitle }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("testBank.number.question") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ exam.bankExam.questionList?.length }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("testBank.number.taker") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ exam.roomExamUserCnt }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("testBank.period") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ exam.beginDate }} ~ {{ exam.endDate }}</div>
        </div>
        <div class="flex">
          <div class="w-28 py-2 px-3 text-sm">{{ $t("testBank.limit") }}</div>
          <div class="min-w-0 flex-1 py-2 px-3 text-sm">{{ examDuration(exam) }}</div>
        </div>
        <div class="py-2 px-3 flex justify-center">
          <GrmBtn class="flex-1" size="sm" @click="onSelectTestbankExamHistory(exam)">{{ $t("setting.disclosure.password.see") }}</GrmBtn>
        </div>
      </div>
    </div>
    <div v-if="!testbankExamHistoryList?.content?.length" class="lg:hidden">
        <p class="text-center p-2 rounded bg-gray-100 text-sm">{{ $t("testBank.noHistory") }}</p>
    </div>
    <GrmPagination class="mt-3"
                   :pageInfo="testbankExamHistoryList?.pageInfo"
                   @paging="(page) => { getTestbankExamHistoryList({ page }) }"
    />
  </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";

import GrmBtn from "@/components/Common/button/GrmBtn.vue";
import GrmPagination from "@/components/Common/pagination/GrmPagination.vue";

export default {
  name: 'RoomTestbankHistoryListTable',
  components: {
    GrmBtn,
    GrmPagination,
  },
  computed: {
    ...mapState('testbank', [
      'testbankExamHistoryList',
      'testbankExamHistoryListFilter',
    ]),

    examNum() {
      return i => (this.testbankExamHistoryList.pageInfo.currentPageNum - 1) * this.testbankExamHistoryListFilter.size + i +  1;
    },
    examType() {
      return exam => exam.examType === 'QUIZ' ? this.$t('common.quiz') : this.$t('common.survey');
    },
    examDuration() {
      return exam => {
        const duration = exam.examDuration;
        const hour = Math.floor(duration / 3600);
        const minute = Math.floor((duration % 3600) / 60);
        const second = duration % 60;
        return `${hour ? hour + this.$t('common.hour') + ' ' : ''}${minute ? minute + this.$t('common.minute') + ' ' : ''}${second ? second + this.$t('common.second') : ''}`;
      }
    }
  },
  created() {
    this.getTestbankExamHistoryList();
  },
  methods: {
    ...mapMutations('testbank', [
      'SET_VIEW',
      'SET_TESTBANK_EXAM_HISTORY',
    ]),
    ...mapActions('testbank', [
      'getTestbankExamHistoryList',
    ]),

    onSelectTestbankExamHistory(exam) {
      this.SET_TESTBANK_EXAM_HISTORY(exam)
      this.SET_VIEW('history__exam');
    }
  }
}
</script>

<style lang="postcss" scoped>
  table { @apply w-full text-sm text-center table-fixed }
  tr { @apply h-10 border-b border-gray-100 }
  th { @apply px-3 text-xs }
  td { @apply px-3 text-xs truncate }
</style>